<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { ChevronUp, ChevronDown } from '@lucide/vue'
import { useFormControl } from '../../composables/useFormControl'
import { useBoundValue } from '../../composables/useBoundValue'

defineOptions({ inheritAttrs: false })
const props = withDefaults(defineProps<{
  value?: number | null
  defaultValue?: number
  min?: number
  max?: number
  step?: number
  precision?: number
  disabled?: boolean
  readonly?: boolean
  placeholder?: string
  size?: 'small' | 'middle' | 'large'
  status?: 'error' | 'warning'
  controls?: boolean
}>(), { min: -Infinity, max: Infinity, step: 1, disabled: undefined, readonly: false, placeholder: '', controls: true })
const emit = defineEmits<{
  'update:value': [value: number | null]
  change: [value: number | null]
  step: [value: number, info: { offset: number; type: 'up' | 'down' }]
  blur: [event: FocusEvent]
  focus: [event: FocusEvent]
  pressEnter: [event: KeyboardEvent]
}>()
const { attrs, disabled, size, status, controlAttrs, onFieldChange, onFieldBlur } = useFormControl(props)
const inputRef = ref<HTMLInputElement | null>(null)
const innerValue = ref<number | null>(props.defaultValue ?? null)
const mergedValue = useBoundValue(props, 'value', innerValue)
const value = computed(() => mergedValue.value ?? null)
const displayValue = ref(format(value.value))
function format(value: number | null) { return value === null ? '' : props.precision === undefined ? String(value) : value.toFixed(props.precision) }
watch(value, next => { if (Number(displayValue.value) !== next || displayValue.value.trim() === '') displayValue.value = format(next) })
const canIncrease = computed(() => !disabled.value && !props.readonly && (value.value === null || value.value < props.max))
const canDecrease = computed(() => !disabled.value && !props.readonly && (value.value === null || value.value > props.min))
function normalize(value: number) {
  const clamped = Math.max(props.min, Math.min(props.max, value))
  return props.precision === undefined ? clamped : Number(clamped.toFixed(props.precision))
}
function update(next: number | null) {
  if (next === value.value) return
  innerValue.value = next
  emit('update:value', next)
  emit('change', next)
  return onFieldChange()
}
function handleStep(type: 'up' | 'down') {
  if (type === 'up' ? !canIncrease.value : !canDecrease.value) return
  const offset = props.step * (type === 'up' ? 1 : -1)
  const next = normalize(Number(((value.value ?? 0) + offset).toPrecision(15)))
  displayValue.value = format(next)
  update(next)
  emit('step', next, { offset: props.step, type })
}
function handleInput(event: Event) {
  if (disabled.value || props.readonly) return
  displayValue.value = (event.target as HTMLInputElement).value
  const text = displayValue.value.trim()
  if (!text) return update(null)
  const next = Number(text)
  if (Number.isFinite(next)) return update(next)
}
function commit() {
  if (disabled.value || props.readonly) return
  const next = displayValue.value.trim() ? Number(displayValue.value) : null
  if (next === null || Number.isFinite(next)) {
    const normalized = next === null ? null : normalize(next)
    displayValue.value = format(normalized)
    update(normalized)
  } else displayValue.value = format(value.value)
}
function handleBlur(event: FocusEvent) { commit(); emit('blur', event); return onFieldBlur() }
function handleKeyDown(event: KeyboardEvent) {
  if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
    event.preventDefault()
    handleStep(event.key === 'ArrowUp' ? 'up' : 'down')
  } else if (event.key === 'Enter' && !event.isComposing) { commit(); emit('pressEnter', event) }
}
defineExpose({ focus: (options?: FocusOptions) => inputRef.value?.focus(options), blur: () => inputRef.value?.blur() })
</script>

<template>
  <div class="rr-input-number" :class="[attrs.class, `rr-input-number--${size}`, { 'is-disabled': disabled, 'is-error': status === 'error', 'is-warning': status === 'warning' }]" :style="attrs.style as any">
    <input ref="inputRef" v-bind="controlAttrs()" type="text" inputmode="decimal" role="spinbutton" class="rr-input-number__input" :value="displayValue" :placeholder="placeholder" :disabled="disabled" :readonly="readonly" :aria-valuemin="Number.isFinite(min) ? min : undefined" :aria-valuemax="Number.isFinite(max) ? max : undefined" :aria-valuenow="value ?? undefined" @input="handleInput" @blur="handleBlur" @focus="emit('focus', $event)" @keydown="handleKeyDown" />
    <div v-if="controls" class="rr-input-number__handlers">
      <button type="button" class="rr-input-number__handler rr-input-number__handler-up" :disabled="!canIncrease" tabindex="-1" aria-label="增加" @mousedown.prevent @click="handleStep('up')"><ChevronUp :size="12" aria-hidden="true" /></button>
      <button type="button" class="rr-input-number__handler rr-input-number__handler-down" :disabled="!canDecrease" tabindex="-1" aria-label="减少" @mousedown.prevent @click="handleStep('down')"><ChevronDown :size="12" aria-hidden="true" /></button>
    </div>
  </div>
</template>

<style scoped>
.rr-input-number.is-warning { border-color: var(--rr-color-warning); }
.rr-input-number.is-warning:focus-within:not(.is-disabled) { border-color: var(--rr-color-warning); box-shadow: 0 0 0 3px var(--rr-color-warning-soft); }
.rr-input-number {
  position: relative;
  display: inline-flex;
  width: 100%;
  box-sizing: border-box;
  border: 1px solid var(--rr-color-border-strong, #cbd2da);
  border-radius: var(--rr-radius-control, 6px);
  background: var(--rr-color-surface, #ffffff);
  transition: all 120ms ease;
  overflow: hidden;
}

.rr-input-number:hover:not(.is-disabled) {
  border-color: var(--rr-color-text-subtle, #8a94a2);
}

.rr-input-number:focus-within:not(.is-disabled) {
  border-color: var(--rr-color-primary, #5a46e0);
  box-shadow: 0 0 0 3px var(--rr-color-primary-soft, #f0edfc);
}

.rr-input-number--small {
  height: 24px;
}

.rr-input-number--middle {
  height: 32px;
}

.rr-input-number--large {
  height: 40px;
}

.rr-input-number__input {
  flex: 1;
  width: 100%;
  height: 100%;
  padding: 0 8px !important;
  margin: 0 !important;
  border: 0 !important;
  outline: 0 !important;
  background: transparent !important;
  color: var(--rr-color-text, #2b3038);
  font-size: var(--rr-font-size-base, 13px);
  box-shadow: none !important;
  box-sizing: border-box;
}

.rr-input-number__input:disabled {
  color: var(--rr-color-text-subtle, #8a94a2);
  cursor: not-allowed;
}

.rr-input-number__handlers {
  display: flex;
  flex-direction: column;
  width: 22px;
  border-left: 1px solid var(--rr-color-border, #e1e5ea);
  opacity: 0;
  transition: opacity 120ms ease;
}

.rr-input-number:hover:not(.is-disabled) .rr-input-number__handlers {
  opacity: 1;
}

.rr-input-number__handler {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex: 1;
  padding: 0 !important;
  margin: 0 !important;
  border: 0 !important;
  background: var(--rr-color-surface-subtle, #f7f8fa) !important;
  color: var(--rr-color-text-muted, #67717f) !important;
  cursor: pointer;
  min-height: unset !important;
  max-height: unset !important;
  box-shadow: none !important;
  transition: all 120ms ease;
}

.rr-input-number__handler:hover:not(:disabled) {
  background: var(--rr-color-primary-soft, #f0edfc) !important;
  color: var(--rr-color-primary, #5a46e0) !important;
}

.rr-input-number__handler:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.rr-input-number__handler-up {
  border-bottom: 1px solid var(--rr-color-border, #e1e5ea) !important;
}

.rr-input-number.is-disabled {
  background: var(--rr-color-canvas, #f3f5f7);
  border-color: var(--rr-color-border, #e1e5ea);
  cursor: not-allowed;
}
</style>
